import React, { useId, useRef, useState } from 'react'
import _ from 'lodash'
import { cn } from '../../lib/utils'
import { useNav } from './NavProvider'
import { getIcon } from './icons'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import SubNav from './SubNav'
import PopupSubNav from './PopupSubNav'
import Link from './Link'
import RateUsFeedback from '../RateUsFeedback/index'
import { useIsMobile } from '~/hooks/useMediaQuery'
import { useMenuKeyboard } from '~/hooks/useMenuKeyboard'

const NavLink = ({ item, theme }) => {
  const { activeNav, hasProgramLevel, meetsMinimumProgramLevel, isExpanded } =
    useNav()
  const { slug, exactProgramLevel, programLevel, hideCollapsed, isMobileMenu } =
    item

  if (!_.isEmpty(exactProgramLevel) && !hasProgramLevel(exactProgramLevel)) {
    return null
  }
  if (
    !meetsMinimumProgramLevel(programLevel) ||
    (!isExpanded && hideCollapsed)
  ) {
    return null
  }
  let customInactiveClasses = ''
  switch (theme) {
    case 'green':
      customInactiveClasses =
        'hover:bg-intoo-green-coachcta-hover hover:text-white focus:bg-intoo-green-coachcta-hover focus:text-white'
      break
    case 'deloitte':
      customInactiveClasses =
        'text-black hover:bg-deloitte hover:text-white focus:text-white focus:bg-deloitte'
      break
    case 'advantage':
      customInactiveClasses =
        'hover:bg-[#E0EFFE] focus:bg-[#E0EFFE] focus:text-cod-gray-800'
  }
  let customActiveClasses = ''
  switch (theme) {
    case 'green':
      customActiveClasses = 'bg-intoo-green-coachcta text-white'
      break
    case 'deloitte':
      customActiveClasses =
        'bg-black text-white hover:bg-deloitte focus:bg-deloitte'
      break
    case 'advantage':
      customActiveClasses =
        'hover:bg-primary-button-darker-75 focus:bg-primary-button-darker-75 text-white hover:text-white focus:text-white'
  }
  if (isMobileMenu) {
    customInactiveClasses = cn('md:hidden', customInactiveClasses)
  }

  return (
    <NavLinkInner
      active={slug === activeNav}
      item={item}
      programLevel={programLevel}
      className={customInactiveClasses}
      customActiveClasses={customActiveClasses}
      theme={theme}
    />
  )
}
export default NavLink

const defaultInactiveClasses =
  'text-normal flex cursor-pointer items-center px-3 py-2.5 font-medium leading-5 tracking-tight text-cod-gray-800 hover:bg-cod-gray-100 rounded-lg hover:border-none hover:text-cod-gray-800 focus:outline-none focus:bg-cod-gray-200 focus:text-cod-gray-800 mx-4'
const defaultActiveClasses = cn(
  defaultInactiveClasses,
  'bg-intoo-blue-medium hover:border-intoo-blue-medium hover:bg-primary-button-darker-75 text-white hover:text-white focus:bg-primary-button-darker-75 focus:text-white',
)

export const NavLinkInner = ({
  active,
  item,
  programLevel,
  className,
  customActiveClasses,
  theme,
}) => {
  const {
    navigateTo,
    setActiveNav,
    isExpanded,
    currentPageNavItem,
    firstName,
    locale,
    platformName,
    popupSubNav,
    setPopupSubNav,
    currentTimer,
  } = useNav()
  const { title, children, slug, data, isMobileMenu } = item
  const popupRef = useRef(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const containerRef = useRef(null)
  const menuId = useId()
  const Icon = getIcon(slug)
  const hasSubmenu = Array.isArray(children) && children.length > 0

  const activeClasses = cn(defaultActiveClasses, className, customActiveClasses)
  const inactiveClasses = cn(defaultInactiveClasses, className)

  const [currentPageActive, setCurrentPageActive] = useState(
    slug === currentPageNavItem,
  )

  const isMobile = useIsMobile()

  const handleClick = () => {
    active || currentPageActive ? deactivate() : activate()
    navigateTo(item)
    localStorage.setItem('state:resourcesActivity', JSON.stringify('articles'))
  }
  const activate = () => {
    if (currentPageNavItem === slug) {
      setCurrentPageActive(true)
    }
    setActiveNav(slug)
  }
  const deactivate = () => {
    if (children) {
      if (currentPageNavItem === slug) {
        setCurrentPageActive(false)
      }
      setActiveNav(null)
    }
  }
  const collapsedChildren = children ? children : [item]
  const activeItem = active || currentPageActive

  const openExpandedMenu = () => {
    if (!activeItem) activate()
  }
  const closeExpandedMenu = () => {
    if (activeItem) deactivate()
  }

  const { handleTriggerKeyDown, handleMenuItemKeyDown } = useMenuKeyboard({
    isOpen: hasSubmenu && (isExpanded ? activeItem : popupSubNav === slug),
    onOpen: hasSubmenu
      ? isExpanded
        ? openExpandedMenu
        : () => setPopupSubNav(slug)
      : () => {},
    onClose: hasSubmenu
      ? isExpanded
        ? closeExpandedMenu
        : () => setPopupSubNav(null)
      : () => {},
    menuRef,
    triggerRef,
    menuContainerRef: isExpanded ? containerRef : popupRef,
    // The expanded nav submenu is an inline accordion that reflects the
    // active page, not a transient popup. Clicking elsewhere on the page
    // must not collapse it / clear the selection. Only the collapsed
    // hover popup should close on an outside click.
    closeOnOutsideClick: !isExpanded,
  })

  if (isMobileMenu && !isMobile) return null

  const linkProps = {}
  if (!_.isEmpty(data)) {
    Object.entries(data).forEach(([key, value]) => {
      linkProps[`data-${key}`] = value
    })
  }
  if (!_.isEmpty(item.path)) {
    linkProps.href = item.path
  } else {
    linkProps.onClick = handleClick
  }

  const navLinkClassName =
    active || currentPageNavItem === slug ? activeClasses : inactiveClasses

  const expandedSubmenuTrigger = (
    <>
      {Icon && (
        <Icon className="mr-2 inline-block h-6 w-6" aria-hidden="true" />
      )}
      {isMobile && slug === 'settings' ? firstName : title}
      <ChevronDownIcon
        className={cn(
          'ml-auto inline-block h-4 w-4 stroke-2 transition-all duration-500',
          activeItem && 'rotate-180',
        )}
        aria-hidden="true"
      />
    </>
  )

  const handleCollapsedMouseOver = () => {
    if (!popupRef.current) return
    const top = triggerRef.current?.getBoundingClientRect().top
    if (top != null) {
      const popupHeight =
        popupRef.current.children[0]?.getBoundingClientRect().height
      if (popupHeight > window.innerHeight - top) {
        popupRef.current.children[0].style.top = `${
          window.innerHeight - popupHeight
        }px`
      } else {
        popupRef.current.children[0].style.top = `${top}px`
      }
    }
    setPopupSubNav(slug)
    clearTimeout(currentTimer.current)
  }

  const handleCollapsedMouseOut = () => {
    clearTimeout(currentTimer.current)
    // eslint-disable-next-line react-hooks/immutability -- shared nav timer ref from context
    currentTimer.current = setTimeout(() => {
      setPopupSubNav(null)
    }, 1000)
  }

  if (isExpanded) {
    return isMobile && slug === 'feedback' ? (
      <RateUsFeedback
        locale={locale}
        program_level={programLevel}
        is_mobile_menu={true}
        btn_text={title}
        platform_name={platformName}
      />
    ) : hasSubmenu ? (
      <div ref={containerRef} className="contents">
        <button
          ref={triggerRef}
          type="button"
          className={cn(navLinkClassName, 'border-0 text-left')}
          aria-haspopup="menu"
          aria-expanded={activeItem}
          aria-controls={menuId}
          onClick={handleClick}
          onKeyDown={handleTriggerKeyDown}
        >
          {expandedSubmenuTrigger}
        </button>

        <SubNav
          ref={menuRef}
          id={menuId}
          items={children}
          theme={theme}
          parentActive={activeItem}
          aria-hidden={!activeItem}
          onMenuItemKeyDown={handleMenuItemKeyDown}
          className={cn(
            'transition-all duration-500',
            activeItem ? 'visible max-h-screen' : 'hidden max-h-0',
          )}
        />
      </div>
    ) : (
      <Link className={navLinkClassName} {...linkProps}>
        {Icon && (
          <Icon className="mr-2 inline-block h-6 w-6" aria-hidden="true" />
        )}
        {isMobile && slug === 'settings' ? firstName : title}
      </Link>
    )
  }

  if (hasSubmenu) {
    return (
      <>
        <PopupSubNav
          popupRef={popupRef}
          menuRef={menuRef}
          menuId={menuId}
          slug={slug}
          items={collapsedChildren}
          isOpen={popupSubNav === slug}
          onMenuItemKeyDown={handleMenuItemKeyDown}
        />
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            active ? activeClasses : inactiveClasses,
            'border-0 text-left',
          )}
          title={title}
          aria-haspopup="menu"
          aria-expanded={popupSubNav === slug}
          aria-controls={menuId}
          onClick={handleClick}
          onKeyDown={handleTriggerKeyDown}
          onMouseOver={handleCollapsedMouseOver}
          onMouseOut={handleCollapsedMouseOut}
        >
          {Icon && <Icon className="inline-block h-6 w-6" aria-hidden="true" />}
        </button>
      </>
    )
  }

  return (
    <>
      <PopupSubNav
        popupRef={popupRef}
        slug={slug}
        items={collapsedChildren}
        isOpen={popupSubNav === slug}
      />
      <Link
        className={active ? activeClasses : inactiveClasses}
        {...linkProps}
        title={title}
        popupRef={popupRef}
        slug={slug}
      >
        {Icon && <Icon className="inline-block h-6 w-6" aria-hidden="true" />}
      </Link>
    </>
  )
}
